/* eslint-disable react-native/no-color-literals */

import React, { useCallback, useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, {
  Easing,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';

import { theme } from '@garnish/constants';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarNewContentIndicator = () => {
  const animationSV = useSharedValue(0);

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  const startAnimation = useCallback(() => {
    animationSV.value = withRepeat(
      withTiming(1, { duration: ANIMATION_DURATION, easing: Easing.linear }),
      -1,
    );
  }, [animationSV]);

  const stopAnimation = useCallback(() => {
    animationSV.value = 0;
  }, [animationSV]);

  const triggerAnimation = useCallback(() => {
    startAnimation();

    return stopAnimation;
  }, [startAnimation, stopAnimation]);

  // ─── Animated Styles ─────────────────────────────────────────────────────────

  const firstRingAnimatedStyle = useAnimatedStyle(() =>
    getRingAnimatedStyle(animationSV.value, FIRST_RING_OFFSET),
  );

  const secondRingAnimatedStyle = useAnimatedStyle(() =>
    getRingAnimatedStyle(animationSV.value, SECOND_RING_OFFSET),
  );

  const thirdRingAnimatedStyle = useAnimatedStyle(() =>
    getRingAnimatedStyle(animationSV.value, THIRD_RING_OFFSET),
  );

  const dotAnimatedStyle = useAnimatedStyle(() => {
    const scale = interpolate(animationSV.value, DOT_PROGRESS, DOT_SCALE);

    return { transform: [{ scale }] };
  });

  // ─── Styles ──────────────────────────────────────────────────────────────────

  const circleStyles = [styles.dot, dotAnimatedStyle];
  const firstRingStyles = [styles.ring, firstRingAnimatedStyle];
  const secondRingStyles = [styles.ring, secondRingAnimatedStyle];
  const thirdRingStyles = [styles.ring, thirdRingAnimatedStyle];

  // ─── Effects ─────────────────────────────────────────────────────────────────

  useEffect(triggerAnimation, [triggerAnimation]);

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container} pointerEvents="none">
      <View style={styles.innerContainer}>
        <Animated.View style={circleStyles} />
      </View>

      <View style={styles.innerContainer}>
        <Animated.View style={firstRingStyles} />
      </View>

      <View style={styles.innerContainer}>
        <Animated.View style={secondRingStyles} />
      </View>

      <View style={styles.innerContainer}>
        <Animated.View style={thirdRingStyles} />
      </View>
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const RING_SIZE = 24;
const RING_COLOR = `rgb(221, 245, 82)`;

const ANIMATION_DURATION = 2400;

const FIRST_RING_OFFSET = 0;
const SECOND_RING_OFFSET = 0.33;
const THIRD_RING_OFFSET = 0.66;

// Small pulse for the central dot.
const DOT_PROGRESS = [0, 0.5, 1];
const DOT_SCALE = [1, 1.15, 1];

// Larger pulse for the rings, becoming transparent in the end of the ripple.
const RING_PROGRESS = [0, 0.5, 1];
const RING_SCALE = [0.5, 0.65, 1.5];
const RING_OPACITY = [0, 0.75, 0];

const getRingAnimatedStyle = (animationProgress: number, offset: number) => {
  'worklet';

  // To stagger the pulse animations.
  const ringProgress = (animationProgress + offset) % 1;

  return {
    transform: [{ scale: interpolate(ringProgress, RING_PROGRESS, RING_SCALE) }],
    opacity: interpolate(ringProgress, RING_PROGRESS, RING_OPACITY),
  };
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: -theme.spacing['1'],
    right: -theme.spacing['2'],
    width: RING_SIZE,
    height: RING_SIZE,
  },
  innerContainer: {
    ...StyleSheet.absoluteFill,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ring: {
    width: RING_SIZE,
    height: RING_SIZE,
    borderColor: RING_COLOR,
    borderRadius: RING_SIZE,
    borderWidth: 1,
    ...theme.elevations['2'],
  },
  dot: {
    width: RING_SIZE / 2,
    height: RING_SIZE / 2,
    borderRadius: RING_SIZE / 2,
    backgroundColor: RING_COLOR,
  },
});
